import { logSchemaFallback } from "_shared/_utilities/log-schema-failure";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { DocumentUploadFailureCode, documentUploadFailureCodes } from "documents/_api/document-upload-validation";
import { z } from "zod";

export type UnavailableDocumentJSON = z.infer<typeof UnavailableDocumentSchema.UnavailableDocumentJSONZ>;
export type UnavailableDocumentParams = z.infer<typeof UnavailableDocumentSchema.UnavailableDocumentZ>;

/**
 * A refusal code the API has started sending that this bundle predates (#6593).
 *
 * The frontend ships to CloudFront and the API to ECS, so a deploy is never atomic and users hold
 * cached bundles besides (see `logSchemaFallback`). A closed enum threw on `duplicate_document` and
 * cost the uploader every file's answer, the server's own `issue` with them. An unknown code reads as
 * `upload_failed`, so the refusal still reaches the uploader in the server's words.
 */
const degradeUnrecognizedCode = ({ value }: { value: unknown }): DocumentUploadFailureCode => {
  logSchemaFallback({
    schemaName: "UnavailableDocumentSchema",
    field: "code",
    value,
    fallback: "upload_failed",
  });
  return "upload_failed";
};

export default class UnavailableDocumentSchema {
  public static readonly UnavailableDocumentJSONZ = z.object({
    id: z.uuid(),
    issue: z.string(),
    status: z.literal("refused").optional(),
    code: z.enum(documentUploadFailureCodes).optional().catch(degradeUnrecognizedCode),
  });
  public static readonly UnavailableDocumentZ = z.object({
    id: z.instanceof(Guid),
    issue: z.string(),
    status: z.literal("refused").optional(),
    code: z.enum(documentUploadFailureCodes).optional(),
  });

  public static validateJSON(values: unknown): UnavailableDocumentParams {
    return parseOrThrow(UnavailableDocumentSchema.UnavailableDocumentZ, values, {
      logError: true,
      schemaName: "UnavailableDocumentSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is UnavailableDocumentParams {
    try {
      UnavailableDocumentSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: UnavailableDocumentJSON): UnavailableDocumentParams {
    const parsed = parseOrThrow(UnavailableDocumentSchema.UnavailableDocumentJSONZ, data, {
      logError: true,
      schemaName: "UnavailableDocumentSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return UnavailableDocumentSchema.codec.decode(parsed);
  }

  public static serialize(document: UnavailableDocumentParams): UnavailableDocumentJSON {
    const parsed = UnavailableDocumentSchema.validateJSON(document);
    return UnavailableDocumentSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    UnavailableDocumentSchema.UnavailableDocumentJSONZ,
    UnavailableDocumentSchema.UnavailableDocumentZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        issue: data.issue,
        status: data.status,
        code: data.code,
      }),
      encode: (document) => ({
        id: document.id.toJSON(),
        issue: document.issue,
        status: document.status,
        code: document.code,
      }),
    }
  );
}
